import type { TokenRequest } from "ably"
import { Mutex } from "async-mutex"
import type {
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react"
import { createApi, retry } from "@reduxjs/toolkit/query/react"
import fetchBaseQuery from "./fetchBaseQuery"
import { clearAuth, updateTokens } from "@/auth/slice"
import type { TokenResponse } from "@/auth/model"
import { getRefreshToken } from "@/auth/refreshTokenStorage"

export type BaseQueryExtraOptions = {
  readonly withoutAuth?: boolean
}

type Assert = (condition: boolean) => asserts condition

const getResponseCode = (
  error: FetchBaseQueryError | undefined,
): number | undefined => {
  if (!error) {
    return undefined
  }

  const status = error.status

  if (status === "PARSING_ERROR") {
    return error.originalStatus
  }

  if (typeof status != "number") {
    return undefined
  }

  return status
}

const mutex = new Mutex()

const baseQuery = retry(
  async (
    args: string | FetchArgs,
    api,
    extraOptions?: BaseQueryExtraOptions,
  ) => {
    const { dispatch, endpoint } = api
    const isTokenRefresh = endpoint === "tokenRefresh"

    const baseQuery = fetchBaseQuery({
      withoutAuth: !!extraOptions?.withoutAuth,
    })

    if (!isTokenRefresh) {
      // Wait for a possible concurrent token refresh
      await mutex.waitForUnlock()
    }

    const result = await baseQuery(args, api, extraOptions ?? {})

    // 401 suggests token has expired, so we try to fetch a new one – or clear auth state if it fails
    // If mutex is locked then another request is already refreshing so we let this one fail and go for retry
    // This be tightly coupled with retry options below – backoff and retryCondition
    if (
      getResponseCode(result.error) === 401 &&
      !mutex.isLocked() &&
      !isTokenRefresh
    ) {
      // Lock and attempt token refresh
      const release = await mutex.acquire()
      try {
        const giveUp = (): never => {
          dispatch(clearAuth())
          retry.fail(result.error)
        }

        const assert: Assert = (condition) => {
          if (!condition) {
            giveUp()
          }
        }

        const refreshToken = getRefreshToken()
        assert(!!refreshToken)

        try {
          // Refresh token
          await dispatch(
            baseApi.endpoints.tokenRefresh.initiate(refreshToken),
          ).unwrap()
        } catch {
          giveUp()
        }
      } finally {
        release()
      }
    }

    return result
  },
  {
    backoff: async (attempt, maxRetries) => {
      if (attempt === 1) {
        // No waiting for first retry to allow instant recovery after token refresh
        return
      }

      // Below is the defaultBackoff copied from RTK as it is not exported

      const attempts = Math.min(attempt, maxRetries) - 1

      const timeout = (Math.random() + 0.4) * (300 << attempts)
      await new Promise<void>((resolve) =>
        setTimeout(() => {
          resolve()
        }, timeout),
      )
    },
    retryCondition: (error, _args, { attempt }) => {
      // bail out of re-tries for client-side errors like validation
      // Except unauth where we rely on automatic refresh and retry
      const responseCode = getResponseCode(
        error as FetchBaseQueryError | undefined,
      )

      const shouldRetry =
        !responseCode ||
        responseCode === 401 ||
        responseCode < 400 ||
        responseCode >= 500

      return shouldRetry && attempt < 3
    },
  },
)

const baseApi = createApi({
  baseQuery,
  refetchOnMountOrArgChange: 60,
  refetchOnReconnect: true,
  endpoints: (build) => ({
    tokenRefresh: build.mutation<TokenResponse, string>({
      query: (payload) => ({
        url: "security/refresh",
        method: "POST",
        body: { refreshToken: payload },
      }),
      extraOptions: { withoutAuth: true },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(updateTokens(data))
        } catch {
          // noop
        }
      },
    }),
    ablyAuth: build.mutation<TokenRequest, void>({
      query: () => ({
        url: "ably-auth",
        method: "POST",
      }),
    }),
  }),
})

export type BaseQuery = typeof baseQuery

export const { useTokenRefreshMutation, useAblyAuthMutation } = baseApi

export default baseApi
